const NAMESPACE = '[PlaybackTiming]';
const STORAGE_KEY = 'cmf:playbackTimingDiagnostics';
const ENV_FLAG_KEYS = [
  'VITE_ENABLE_PLAYBACK_TIMING_DIAGNOSTICS',
  'VITE_FEATURE_PLAYBACK_TIMING_DIAGNOSTICS'
] as const;

type FlagValue = string | boolean | null | undefined;

export type PlaybackTimingEventName =
  | 'environment'
  | 'battery'
  | 'player_state_changed'
  | 'song_progress_render'
  | 'page_visibility';

type PlaybackTimingPayload = Record<string, unknown>;

let lastTimestampRef: number | null = null;
let environmentLogged = false;

const isTruthy = (value: FlagValue): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
  }
  return false;
};

const readEnvFlag = (): boolean => {
  try {
    if (typeof import.meta !== 'undefined') {
      const env = (import.meta as ImportMeta).env;
      if (env) {
        for (const key of ENV_FLAG_KEYS) {
          const value = env[key];
          if (value !== undefined && value !== null) {
            if (isTruthy(value)) {
              return true;
            }
            if (!isTruthy(value)) {
              return false;
            }
          }
        }
      }
    }
  } catch {
    // ignore import.meta access errors outside browser environments
  }
  if (typeof process !== 'undefined' && process.env) {
    for (const key of ENV_FLAG_KEYS) {
      const value = (process.env as Record<string, FlagValue>)[key];
      if (value !== undefined && value !== null) {
        return isTruthy(value);
      }
    }
  }
  return false;
};

const readWindowFlag = (): boolean | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const explicit = (window as WindowWithDiagnostics).__CMF_PLAYBACK_TIMING_DIAGNOSTICS__;
  if (typeof explicit === 'boolean') {
    return explicit;
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored == null) {
      return null;
    }
    return isTruthy(stored);
  } catch (error) {
    if (import.meta.env?.DEV) {
      console.warn(`${NAMESPACE} Unable to read playback timing diagnostics flag from storage`, error);
    }
    return null;
  }
};

const setWindowFlag = (enabled: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (enabled) {
      window.localStorage.setItem(STORAGE_KEY, 'true');
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    if (import.meta.env?.DEV) {
      console.warn(`${NAMESPACE} Unable to persist playback timing diagnostics flag`, error);
    }
  }
  (window as WindowWithDiagnostics).__CMF_PLAYBACK_TIMING_DIAGNOSTICS__ = enabled;
};

const ensureWindowHelpers = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const win = window as WindowWithDiagnostics;
  if (win.__CMF_SET_PLAYBACK_TIMING_DIAGNOSTICS__) {
    return;
  }
  win.__CMF_SET_PLAYBACK_TIMING_DIAGNOSTICS__ = (enabled: boolean) => {
    setWindowFlag(Boolean(enabled));
    const status = enabled ? 'enabled' : 'disabled';
    console.info(`${NAMESPACE} diagnostics ${status} via window helper`);
  };
};

const shouldLog = (): boolean => {
  const envEnabled = readEnvFlag();
  if (envEnabled) {
    return true;
  }

  const windowFlag = readWindowFlag();
  if (typeof windowFlag === 'boolean') {
    return windowFlag;
  }

  return false;
};

const emitLog = (event: PlaybackTimingEventName, payload: PlaybackTimingPayload) => {
  const now = Date.now();
  const isoTimestamp = new Date(now).toISOString();
  const elapsedSinceLast = lastTimestampRef != null ? now - lastTimestampRef : null;
  lastTimestampRef = now;

  const entry = {
    at: isoTimestamp,
    elapsedSinceLastMs: elapsedSinceLast,
    ...payload
  };

  console.info(`${NAMESPACE} ${event}`, entry);
};

const logEnvironmentSnapshot = () => {
  if (environmentLogged) {
    return;
  }
  environmentLogged = true;

  if (typeof navigator !== 'undefined') {
    emitLog('environment', {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      hardwareConcurrency: typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : null,
      deviceMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null,
      onLine: navigator.onLine,
      referrer: typeof document !== 'undefined' ? document.referrer : null
    });

    const connection = (navigator as Navigator & { connection?: unknown }).connection as
      | {
          downlink?: number;
          effectiveType?: string;
          rtt?: number;
          saveData?: boolean;
        }
      | undefined;

    if (connection) {
      emitLog('environment', {
        connection: {
          downlink: typeof connection.downlink === 'number' ? connection.downlink : null,
          effectiveType: connection.effectiveType ?? null,
          rtt: typeof connection.rtt === 'number' ? connection.rtt : null,
          saveData: typeof connection.saveData === 'boolean' ? connection.saveData : null
        }
      });
    }

    const getBattery = (navigator as Navigator & { getBattery?: () => Promise<BatteryStatus> }).getBattery;
    if (typeof getBattery === 'function') {
      getBattery
        .call(navigator)
        .then((battery) => {
          if (!shouldLog()) {
            return;
          }
          emitLog('battery', {
            charging: battery.charging,
            chargingTime: battery.chargingTime,
            dischargingTime: battery.dischargingTime,
            level: battery.level
          });
        })
        .catch(() => {
          // Silently ignore battery API failures; not all browsers support it.
        });
    }
  }
};

export const logPlaybackTimingEvent = (
  event: PlaybackTimingEventName,
  payload: PlaybackTimingPayload = {}
) => {
  if (!shouldLog()) {
    return;
  }
  ensureWindowHelpers();
  logEnvironmentSnapshot();
  emitLog(event, payload);
};

declare global {
  interface Window {
    __CMF_PLAYBACK_TIMING_DIAGNOSTICS__?: boolean;
    __CMF_SET_PLAYBACK_TIMING_DIAGNOSTICS__?: (enabled: boolean) => void;
  }

  interface ImportMeta {
    env?: Record<string, FlagValue>;
  }
}

type BatteryStatus = {
  charging: boolean;
  chargingTime: number;
  dischargingTime: number;
  level: number;
};

type WindowWithDiagnostics = Window & {
  __CMF_PLAYBACK_TIMING_DIAGNOSTICS__?: boolean;
  __CMF_SET_PLAYBACK_TIMING_DIAGNOSTICS__?: (enabled: boolean) => void;
};

export const setPlaybackTimingDiagnostics = (enabled: boolean) => {
  ensureWindowHelpers();
  setWindowFlag(enabled);
  const status = enabled ? 'enabled' : 'disabled';
  console.info(`${NAMESPACE} diagnostics ${status} via direct API`);
};

export const isPlaybackTimingDiagnosticsEnabled = (): boolean => shouldLog();

ensureWindowHelpers();
